import React from 'react';

/**
 * Last-resort error boundary around the whole app. If a rendering error would otherwise
 * blank the screen, show a graceful full-page message with a reload action instead.
 * Deliberately dependency-free (no icons, no ClinicLogo, no API calls) so the fallback
 * can never crash for the same reason the app did.
 */
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Unrecoverable UI error:', error, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-navy-800 via-navy-900 to-navy-950 p-6">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center text-3xl font-bold text-emerald-100">
            !
          </div>
          <h1 className="mt-6 text-2xl font-bold text-white">Something went wrong</h1>
          <p className="mt-3 text-emerald-100 text-sm leading-relaxed">
            The page ran into an unexpected problem. Your saved data is not affected.
            Reloading usually fixes it.
          </p>
          <p className="mt-2 text-emerald-200/90 urdu-text text-base" dir="rtl">
            ایک غیر متوقع مسئلہ پیش آ گیا ہے۔ صفحہ دوبارہ لوڈ کرنے سے عموماً ٹھیک ہو جاتا ہے۔
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 bg-white text-emerald-700 rounded-lg font-semibold text-sm hover:bg-emerald-50"
          >
            Reload Page
          </button>
          {this.state.error?.message ? (
            <p className="mt-8 text-[11px] text-emerald-300/60 font-mono break-all">
              {String(this.state.error.message)}
            </p>
          ) : null}
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
